import { FC } from 'react'
import { Alert } from '@material-tailwind/react'
import ReactAudioPlayer from 'react-audio-player'
import { PiSpeakerX } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'

import { useDarkMode } from '../contexts/DarkModeContext'
import { getAgamiMediaState } from '../utils/helpers/agami.helpers'
import AgamiMediaTypeChip from './AgamiMediaTypeChip'

interface AgamiMediaStatusProps {
  spot: { content_media_type?: number; audio_content_path?: string | null; audioUrl?: string }
  playerClassName: string
  alertClassName: string
  playerTestClass?: string
  showIntentionalNone?: boolean
}

/**
 * The four media states of a card (07.2), keyed on the declared type, path and
 * signed URL. The old `audioUrl ?` condition conflated two different things:
 * a word with no content and a word whose URL failed to generate — and said
 * "missing content" about content that exists, sending the owner to hunt for
 * a problem that is not there. "I must upload it" and "something is broken"
 * are different actions, so they get different messages.
 *
 * ONE component for both cards (AgamiPreviewCard, SharedAgamiCard): same
 * reason AgamiDraftChip is one — two copies of this condition would diverge.
 */
const AgamiMediaStatus: FC<AgamiMediaStatusProps> = ({
  spot,
  playerClassName,
  alertClassName,
  playerTestClass,
  showIntentionalNone = true,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const mediaState = getAgamiMediaState(spot)

  if (mediaState === 'intentional-none') {
    if (!showIntentionalNone) return null

    return (
      <div className={alertClassName}>
        <AgamiMediaTypeChip mediaType={spot.content_media_type} dataTestClass="intentional-no-media" />
      </div>
    )
  }

  if (mediaState === 'awaiting-upload') {
    return (
      <Alert
        color="orange"
        variant={darkMode ? 'outlined' : 'ghost'}
        className={alertClassName}
        icon={<PiSpeakerX className="w-4 h-4" />}
        data-testclass="no-media-alert"
      >
        {t('agami.missingAudio')}
      </Alert>
    )
  }

  if (mediaState === 'available' && spot.audioUrl) {
    return (
      <ReactAudioPlayer
        className={playerClassName}
        src={spot.audioUrl}
        controls
        {...(playerTestClass && { 'data-testclass': playerTestClass })}
      />
    )
  }

  return (
    <Alert
      color="red"
      variant={darkMode ? 'outlined' : 'ghost'}
      className={alertClassName}
      icon={<PiSpeakerX className="w-4 h-4" />}
      data-testclass="media-load-error"
    >
      {t('agami.mediaLoadError')}
    </Alert>
  )
}

export default AgamiMediaStatus
